/**
 * useDocumentExtract — upload an invoice document through the mandatory redaction
 * flow, trigger extraction, subscribe to result.
 *
 * Flow:
 *   1. Caller provides a File + vehicleId. Hook computes SHA-256 client-side.
 *   2. POST /v1/documents/upload-intent → 201 { documentId, uploadUrl, uploadMethod }
 *   3. PUT ${apiBase}${uploadUrl} with raw bytes → 204
 *   4. POST /v1/documents/${documentId}/uploaded → 202 (enqueues redaction worker)
 *   5. Subscribe to `vehicles/${vehicleId}/documents/${documentId}` until
 *      status === "committed"; read redacted sha256.
 *   6. POST /v1/vehicles/${vehicleId}/extract { ref: redactedSha256 } → 202 { resultId }
 *   7. Subscribe to `extractions/${resultId}` via onSnapshot until done/failed.
 *
 * State machine:
 *   idle → uploading → redacting → extracting → done | failed
 *
 * Auth: Bearer Firebase token via auth.currentUser.getIdToken().
 * Firestore onSnapshot reads the same auth context.
 */

import { useFirestoreDoc } from '@/hooks/useFirestoreDoc';
import { auth } from '@/lib/firebase';
import { getFirestoreSdk } from '@/lib/firestoreSdk';
import type { ExtractionDoc, InvoiceExtractionResult } from '@/types/domain';
import { useCallback, useState } from 'react';

/**
 * Hard ceiling on the wait for the redaction worker to produce a terminal
 * document state (committed/failed). Without it, a lost Cloud Task or a stalled
 * worker would hang the upload UI on "Securing…" forever. gg-worker's in-process
 * gg-redact retry keeps the happy path well under this even on a cold start, so
 * exceeding it signals a real problem and we surface a retryable error.
 */
const REDACTION_WAIT_TIMEOUT_MS = 90_000;

export type DocumentExtractState =
  | { status: 'idle' }
  | { status: 'uploading' }
  | { status: 'redacting'; documentId: string }
  | { status: 'extracting'; resultId: string }
  | { status: 'done'; resultId: string; doc: ExtractionDoc; result: InvoiceExtractionResult }
  | { status: 'failed'; error: string };

interface DocumentExtractResult {
  state: DocumentExtractState;
  extract: (file: File) => Promise<void>;
  reset: () => void;
}

/** Shape of the Firestore doc at vehicles/{vehicleId}/documents/{documentId}. */
interface VehicleDocumentDoc {
  id: string;
  status: 'pending' | 'redacting' | 'committed' | 'failed';
  /** Redacted CAS key — present when status === "committed". */
  sha256?: string;
  error?: string;
}

/** Compute lowercase hex SHA-256 of an ArrayBuffer. */
async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function parseErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const parsed = (await response.json()) as { error?: { message?: string } };
    return parsed.error?.message ?? fallback;
  } catch {
    // non-JSON body
    return fallback;
  }
}

function useExtractionDoc(resultId: string | null) {
  return useFirestoreDoc<ExtractionDoc>(resultId ? `extractions/${resultId}` : null);
}

export function useDocumentExtract(vehicleId: string): DocumentExtractResult {
  const [state, setState] = useState<DocumentExtractState>({ status: 'idle' });

  const extractionResultId =
    state.status === 'extracting' || state.status === 'done' ? state.resultId : null;

  const { data: extractionDoc, error: firestoreError } = useExtractionDoc(extractionResultId);

  if (firestoreError && state.status === 'extracting') {
    setState({ status: 'failed', error: firestoreError.message });
  } else if (extractionDoc && state.status === 'extracting') {
    if (extractionDoc.status === 'done') {
      const result = extractionDoc.result as InvoiceExtractionResult | undefined;
      if (!result) {
        setState({ status: 'failed', error: 'Extraction completed but no result data.' });
      } else {
        setState({ status: 'done', resultId: state.resultId, doc: extractionDoc, result });
      }
    } else if (extractionDoc.status === 'failed') {
      setState({ status: 'failed', error: extractionDoc.error ?? 'Extraction failed.' });
    }
    // status === 'pending' — stay in extracting state, keep listening
  }

  const extract = useCallback(
    async (file: File): Promise<void> => {
      setState({ status: 'uploading' });
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Not signed in');

        const token = await user.getIdToken();
        const bytes = await file.arrayBuffer();
        const rawSha256 = await sha256Hex(bytes);
        const contentType = file.type || 'application/octet-stream';

        const apiBase = import.meta.env.VITE_GG_API_BASE_URL as string;

        // Step 1: Request upload intent → get documentId + signed upload URL
        const intentResponse = await fetch(`${apiBase}/v1/documents/upload-intent`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            vehicleId,
            filename: file.name,
            contentType,
            rawSha256,
            sizeBytes: bytes.byteLength,
          }),
        });

        if (!intentResponse.ok) {
          throw new Error(await parseErrorMessage(intentResponse, intentResponse.statusText));
        }

        const { documentId, uploadUrl, uploadMethod } = (await intentResponse.json()) as {
          documentId: string;
          uploadUrl: string;
          uploadMethod: string;
        };

        // Step 2: PUT raw bytes to the upload URL (uploadUrl is a relative path)
        const putResponse = await fetch(`${apiBase}${uploadUrl}`, {
          method: uploadMethod,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': contentType,
          },
          body: bytes,
        });

        if (!putResponse.ok) {
          throw new Error(await parseErrorMessage(putResponse, putResponse.statusText));
        }

        // Step 3: Notify backend that raw upload is complete → enqueues redaction worker
        const uploadedResponse = await fetch(`${apiBase}/v1/documents/${documentId}/uploaded`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!uploadedResponse.ok) {
          throw new Error(await parseErrorMessage(uploadedResponse, uploadedResponse.statusText));
        }

        // Step 4: Wait for redaction to commit via a one-shot Firestore subscription.
        // We subscribe imperatively here (not via useFirestoreDoc) so the async
        // extract() callback can await the result before proceeding to extraction.
        setState({ status: 'redacting', documentId });

        const redactedSha256 = await new Promise<string>((resolve, reject) => {
          let unsub: (() => void) | undefined;
          let settled = false;

          // Hard ceiling: if no terminal snapshot arrives, fail (retryable). The
          // callback inlines the settle logic so it doesn't forward-reference
          // `settle` (which clears this timeout) below.
          const timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            unsub?.();
            reject(
              new Error('Securing your document is taking longer than expected. Please try again.'),
            );
          }, REDACTION_WAIT_TIMEOUT_MS);

          // Settle exactly once: clear the timeout, unsubscribe, then run the
          // resolve/reject. Guards against the timeout racing a late snapshot.
          const settle = (run: () => void) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            unsub?.();
            run();
          };

          void getFirestoreSdk()
            .then(({ sdk, db }) => {
              if (settled) return; // timed out before the dynamic import resolved
              const ref = sdk.doc(db, `vehicles/${vehicleId}/documents/${documentId}`);
              unsub = sdk.onSnapshot(
                ref,
                { includeMetadataChanges: false },
                (snap) => {
                  if (!snap.exists()) return;
                  const data = snap.data() as VehicleDocumentDoc;
                  if (data.status === 'committed') {
                    const sha = data.sha256;
                    settle(() =>
                      sha ? resolve(sha) : reject(new Error('Committed document missing sha256.')),
                    );
                  } else if (data.status === 'failed') {
                    settle(() => reject(new Error(data.error ?? 'Redaction failed.')));
                  }
                  // status === 'pending' | 'redacting' → keep listening
                },
                (err) => settle(() => reject(err)),
              );
            })
            .catch((err: unknown) =>
              settle(() =>
                reject(err instanceof Error ? err : new Error('Redaction subscription failed.')),
              ),
            );
        });

        // Step 5: Trigger extraction using the redacted CAS key
        const extractResponse = await fetch(`${apiBase}/v1/vehicles/${vehicleId}/extract`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ref: redactedSha256 }),
        });

        if (!extractResponse.ok) {
          throw new Error(await parseErrorMessage(extractResponse, extractResponse.statusText));
        }

        const { resultId } = (await extractResponse.json()) as {
          resultId: string;
          taskId: string;
        };
        setState({ status: 'extracting', resultId });
      } catch (err) {
        setState({
          status: 'failed',
          error: err instanceof Error ? err.message : 'Upload failed.',
        });
      }
    },
    [vehicleId],
  );

  const reset = useCallback(() => {
    setState({ status: 'idle' });
  }, []);

  return { state, extract, reset };
}
